import React, {useEffect, useState} from "react";
import '@odx/foundation/loader';
import '@odx/foundation/styles';

import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import {GlobalDictionary} from "../../../Utils/globalDictionary";
import {IApiKey, IApiKeyContentTypes} from "../Interfaces/IApiKeyManagement";
import {getApiKeys} from "../BackendFacade/apiCalls";
import {isUserroleSufficient} from "../../../Utils/authorization";
import {UserRoles} from "../../../Utils";
import {deepCopy, formatDate, replaceProjectGuidWithDisplayName} from "../../../Utils/transformer";
import {convertAuthlevelToTenantsystem} from "../../IDMS/Utils/tenantSystem";
import {IApiKeysAddEditState, IApiKeysEditMode} from "../Interfaces/IApiKeysAddEditState";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import OdxTable from "../../../OdxComponents/NativeOdx/Table/Odx-Table";
import {odxIconSets} from "../../../OdxComponents/constants";
import {OdxTableRow} from "../../../OdxComponents/NativeOdx/Table/Odx-TableRow";
import {OdxTableCell} from "../../../OdxComponents/NativeOdx/Table/Odx-Tablecell";
import OdxAreaHeader from "../../../OdxComponents/NativeOdx/Odx-AreaHeader";
import ApiKeysAddModal from "./Modals/AddApiKeyModal";
import ApiKeysEditModal from "./Modals/EditApiKeyModal";
import {defaultAuth} from "../constants";
import OdxBreadcrumbs from "../../../OdxComponents/NativeOdx/Odx-Breadcrumbs";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";
import {listModuleInstances} from "../../../BackendFacade/moduleCalls";
import {loadAllTenantScope} from "../../IDMS/Utils/tenantSystem";
import {simplifyForHtmlId} from "../../../Utils/transformer";
import OdxContentBox from "../../../OdxComponents/NativeOdx/Odx-Contentbox";

const ApiKeyManagement: React.FC = () => {

    let projectId = "null";
    if (HubPathRouting.currentProject) {
        projectId = HubPathRouting.currentProject.id;
    }

    GlobalDictionary.getProjects();

    const [apiKeys, setApiKeys] = useState<IApiKey[]>([])
    const [addEditModal, setAddEditModal] = useState<IApiKeysAddEditState | undefined>(undefined);
    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const baseId = 'hub-api-keys';

    const {setLoading, createToast, createErrorToast} = useMain();

    useModuleViewTracking('hub-apikeys')

    const ensureProjectModulesLoaded = async () => {
        if (projectId === "null") {
            try {
                await loadAllTenantScope(true, (title, message) => createErrorToast(title, message));
            } catch (error) {
                const message = error instanceof Error ? error.message : "Unknown error";
                createErrorToast("Error loading global modules: " + message);
            }
            return;
        }
        const cacheKey = projectId + GlobalDictionary.MODULEINSTANCES;
        if (GlobalDictionary.get(cacheKey)) {
            return;
        }
        try {
            const response = await listModuleInstances(projectId);
            if (response.success) {
                GlobalDictionary.set(cacheKey, response.data.value);
            } else {
                createErrorToast("Error loading modules: " + response.message);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : "Unknown error";
            createErrorToast("Error loading modules: " + message);
        }
    };

    useEffect(() => {
        void ensureProjectModulesLoaded();
        if (!GlobalDictionary.get("apiKeys" + projectId)) {
            getApiKeysWait();
        } else {
            getBackendApiKeys();
        }
    }, []);

    const getApiKeysWait = async () => {
        setLoading(true);
        await getBackendApiKeys();
    };

    const getBackendApiKeys = async () => {
        getApiKeys(projectId).then((data) => {
            if (data.success) {
                GlobalDictionary.set("apiKeys" + projectId, data.data.value);
                setApiKeys(data.data.value as IApiKey[]);
            } else {
                createErrorToast("Error getting API Keys: " + data.message);
            }
            setLoading(false);
        });
    };

    const EditEntry_show = async (key?: IApiKey) => {
        await ensureProjectModulesLoaded();
        const AddEdit: IApiKeysAddEditState = {
            editMode: undefined,
            tenantSystem: undefined,
            Selected_Value: key,
            listOfSubscopes: [],
            expirationDate: false,
        };
        if (key === undefined) {
            AddEdit.editMode = IApiKeysEditMode.Add;
            AddEdit.Selected_Value = {
                displayName: "",
                secondaryApiKey: "",
                primaryApiKey: "",
                projectId: projectId,
                apiKeyId: "",
                content: "",
                contentType: IApiKeyContentTypes.hubIdmsTenantJson,
                lastUsedOn: "",
                validUntil: "",
            };
            AddEdit.tenantSystem = [{id: 0, scopes: ["user"], value: defaultAuth, tmp: "", fullscope: "user"}];
            setIsAddModalOpen(true);
        } else {
            AddEdit.editMode = IApiKeysEditMode.Edit;
            AddEdit.Selected_Value = deepCopy(key);

            if (key.validUntil !== null && key.validUntil !== "") {
                AddEdit.expirationDate = true;
            }

            if (AddEdit.Selected_Value.contentType === IApiKeyContentTypes.hubIdmsTenantJson) {
                const authLevel = JSON.parse(AddEdit.Selected_Value.content);
                AddEdit.tenantSystem = convertAuthlevelToTenantsystem(authLevel, projectId);
            } else {
                AddEdit.tenantSystem = undefined;
            }
            setIsEditModalOpen(true);
        }
        setAddEditModal(AddEdit);
    };

    let headerData = [
        {name: "displayName", title: "Display Name", sortable: true},
        {
            name: "projectId",
            title: "Project Id",
            sortable: true,
            className: !HubPathRouting.currentProject ? '' : 'd-none'
        },
        {name: "lastUsedOn", title: "Last Used On", sortable: true, className: "alignCenter"},
        {name: "validUntil", title: "Valid Until", sortable: true, className: "alignCenter", required: true},
        {name: "", title: "", className: "alignCenter", required: true},
    ];

    if (projectId !== "null") {
        headerData = headerData.filter((header) => header.name !== "projectId");
    }

    const handleButtonClick = (callback) => {
        return {
            onClick: callback
        };
    };

    const tableRows = apiKeys.map((apiKey, index) => {
        const apiKeyIdentifier = simplifyForHtmlId(apiKey.apiKeyId || apiKey.displayName || `api-key-${index}`);
        const lastUsed = formatDate(apiKey.lastUsedOn) !== "" ? formatDate(apiKey.lastUsedOn) : "Never";
        const validUntil = formatDate(apiKey.validUntil) !== "" ? formatDate(apiKey.validUntil) : "-";
        return (
            <OdxTableRow key={apiKey.apiKeyId ?? index.toString()} id={`${baseId}-row-${apiKeyIdentifier}`}>
                <OdxTableCell id={`${baseId}-cell-${apiKeyIdentifier}-name`}>{apiKey.displayName}</OdxTableCell>
                {!HubPathRouting.currentProject &&
                    <OdxTableCell id={`${baseId}-cell-${apiKeyIdentifier}-project`}>
                        {replaceProjectGuidWithDisplayName(apiKey.projectId)}
                    </OdxTableCell>
                }
                <OdxTableCell className={"text-center align-middle"} id={`${baseId}-cell-${apiKeyIdentifier}-last-used`}>
                    {lastUsed}
                </OdxTableCell>
                <OdxTableCell className={"text-center align-middle"} id={`${baseId}-cell-${apiKeyIdentifier}-valid-until`}>
                    {validUntil}
                </OdxTableCell>
                <OdxTableCell className={"text-center align-middle"} id={`${baseId}-cell-${apiKeyIdentifier}-actions`}>
                    <odx-button
                        id={`${baseId}-details-btn-${apiKeyIdentifier}`}
                        variant="primary"
                        {...handleButtonClick(() => EditEntry_show(apiKey))}
                    >
                        <odx-icon name="chevron-right" size="md"></odx-icon>
                    </odx-button>
                </OdxTableCell>
            </OdxTableRow>
        );
    });

    return <>
        {isAddModalOpen && addEditModal && (
            <ApiKeysAddModal
                toggleOpen={() => setIsAddModalOpen(false)}
                closeFunction={() => setIsAddModalOpen(false)}
                onSuccessFunction={() => {
                    setIsAddModalOpen(false);
                    getApiKeysWait();
                }}
                projectId={projectId}
            />
        )}
        {isEditModalOpen && addEditModal && (
            <ApiKeysEditModal
                toggleOpen={() => setIsEditModalOpen(false)}
                closeFunction={() => setIsEditModalOpen(false)}
                onSuccessFunction={() => {
                    setIsEditModalOpen(false);
                    getApiKeysWait();
                }}
                projectId={projectId}
                selectedValue={addEditModal}
            />
        )}
        <div id={`${baseId}-view`}>
            <OdxBreadcrumbs id={`${baseId}-breadcrumbs`}/>
            <OdxAreaHeader
                text={"Api Keys"}
                subTitle={<div id={`${baseId}-count`}>Api Keys: {apiKeys.length}</div>}
                odxContent={
                    <>
                        {isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id) && (
                            <odx-button
                                id={`${baseId}-create-btn`}
                                variant="accent"
                                size="lg"
                                {...handleButtonClick(() => EditEntry_show(undefined))}
                            >
                                <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="md"></odx-icon>
                                Create Api Key
                            </odx-button>
                        )}
                    </>
                }
                className={'!pl-0 !pr-0'}
                id={`${baseId}-header`}
            />
            <OdxContentBox id={`${baseId}-table-container`}>
                <div className={"overflow-y-scroll h-[calc(100vh-240px)]"} id={`${baseId}-table-scroll`}>
                    <OdxTable
                        headerData={headerData}
                        ariaDescribedby="tableOfUsers"
                        className=""
                        variant="striped"
                        id={`${baseId}-table`}
                    >
                        {tableRows}
                    </OdxTable>
                </div>
            </OdxContentBox>
        </div>
    </>;
}

export default ApiKeyManagement;
